import {
  faArrowRight,
  faMagnifyingGlass,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import React from "react";

type InputBoxProps = {
  variant?: "newsletter" | "search";
  success?: string;
  error?: string;
  placeholder?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit?: (value: string) => void;
  className?: string;
  isMobileView?: boolean;
  showSearchLabel?: boolean;
};

export default function InputBox({
  variant = "newsletter",
  success = "",
  error = "",
  placeholder,
  value,
  onChange,
  onSubmit,
  isMobileView = false,
  className,
  showSearchLabel = false,
}: InputBoxProps) {
  const isNewsletter = variant === "newsletter";
  const isSearch = variant === "search";
  const defaultPlaceholder = isSearch ? "China" : "Subscribe with email";

  if (isSearch) {
    return (
      <div
        className={clsx(
          "box-border flex w-full flex-col items-start",
          showSearchLabel && "gap-2.5",
          className,
        )}
      >
        {showSearchLabel && (
          <p className="text-button-1 w-full shrink-0 whitespace-pre-wrap text-black">
            Enter keywords to search
          </p>
        )}
        <div className="flex w-full shrink-0 items-start gap-2">
          <InputField
            placeholder={placeholder || defaultPlaceholder}
            value={value}
            onChange={onChange}
            onSubmit={onSubmit}
          />
          <Button isSearch value={value} onSubmit={onSubmit} />
        </div>
      </div>
    );
  }

  return (
    <div
      className={clsx(
        "box-border flex w-full items-start justify-center gap-2 rounded-[100px]",
        isMobileView && "flex-col! items-center",
        className,
      )}
    >
      <div className="relative flex w-full flex-col gap-2">
        {isMobileView ? (
          <>
            <InputField
              placeholder={placeholder || defaultPlaceholder}
              value={value}
              onChange={onChange}
              onSubmit={onSubmit}
              fullWidthNewsletter
            />
            {isNewsletter && <Messages success={success} error={error} />}
            <Button value={value} onSubmit={onSubmit} isMobileView />
          </>
        ) : (
          <>
            <div className="flex w-full flex-row items-start justify-center gap-2">
              <InputField
                placeholder={placeholder || defaultPlaceholder}
                value={value}
                onChange={onChange}
                onSubmit={onSubmit}
                fullWidthNewsletter
              />
              <Button value={value} onSubmit={onSubmit} />
            </div>
            {isNewsletter && <Messages success={success} error={error} />}
          </>
        )}
      </div>
    </div>
  );
}

function Messages({ success, error }: { success: string; error: string }) {
  if (!success && !error) return null;

  return (
    <div className={clsx("text-label-2 flex flex-col items-start gap-2")}>
      {success && <p className="text-success shrink-0">{success}</p>}
      {error && <p className="text-alert shrink-0">{error}</p>}
    </div>
  );
}

function InputField({
  placeholder,
  value,
  onChange,
  onSubmit,
  fullWidthNewsletter = false,
}: {
  placeholder: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit?: (value: string) => void;
  fullWidthNewsletter?: boolean;
}) {
  return (
    <div
      className={clsx(
        "border-divider-secondary bg-background-default flex h-10 flex-1 shrink-0 items-center justify-between rounded border border-solid px-3 py-2",
        fullWidthNewsletter
          ? "w-full lg:min-h-px lg:min-w-px"
          : "min-h-px min-w-px",
      )}
    >
      <input
        type="text"
        className={clsx(
          "text-body-2 text-primary placeholder:text-primary w-full outline-none",
        )}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onKeyDown={(e) => {
          if (e.key === "Enter" && onSubmit) {
            onSubmit(value || "");
          }
        }}
      />
    </div>
  );
}

function Button({
  isSearch = false,
  value,
  onSubmit,
  isMobileView = false,
}: {
  isSearch?: boolean;
  value?: string;
  onSubmit?: (value: string) => void;
  isMobileView?: boolean;
}) {
  return (
    <button
      onClick={() => {
        if (onSubmit) {
          onSubmit(value || "");
        }
      }}
      className={clsx(
        "bg-button-primary hover:bg-button-primary-hover flex shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-[100px] border-none p-0 transition-all duration-200",
        !isSearch && isMobileView
          ? "h-8 w-full gap-2 px-3 py-2 hover:gap-3 hover:pr-2"
          : "size-10",
      )}
    >
      {!isSearch && isMobileView && (
        <span className="text-button-1 text-white">Subscribe</span>
      )}
      <FontAwesomeIcon
        icon={isSearch ? faMagnifyingGlass : faArrowRight}
        className="size-4.5 text-white"
      />
    </button>
  );
}
